import { useQuery } from '@tanstack/react-query';
import { ApiError, useApi, useAuth } from '../../hooks';

const HTTP_NOT_FOUND = 404;

type CardIdentity = {
  first_name: string | null;
  last_name: string | null;
  member_id: string | null;
};

// Fields this screen reads from GET /private/user/eligibility.
export type UserEligibility = {
  affiliate: string | null;
  group_id: string | null;
  relationship_to_subscriber: string | null;
  patient: CardIdentity;
  subscriber:
    | (CardIdentity & {
        date_of_birth: string | null;
        gender: string | null;
      })
    | null;
};

export const useUserEligibility = () => {
  const { api } = useApi({ throwIfError: true });
  const { userToken } = useAuth();

  const fetchEligibility = async (): Promise<UserEligibility | null> => {
    try {
      return await api({
        token: userToken,
        route: '/private/user/eligibility',
      });
    } catch (error) {
      // A missing record is "no plan", not a failed load.
      if (error instanceof ApiError && error.status === HTTP_NOT_FOUND) {
        return null;
      }
      throw error;
    }
  };

  const { data, isInitialLoading, isError } = useQuery<
    UserEligibility | null,
    Error
  >({
    queryKey: ['user-eligibility', userToken],
    queryFn: fetchEligibility,
    enabled: !!userToken,
    refetchOnWindowFocus: false,
  });

  return {
    eligibility: data,
    // isLoading would stay true while the query waits for a token.
    isLoading: isInitialLoading,
    isError,
  };
};
